import React, { cloneElement, useState } from 'react';

import { Box, Flex, FlexProps, Paper, Space, Text, Title, Tooltip, useMantineTheme } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_RESOURCES } from 'shared/constants/query-keys';
import { Resource, ResourceAiProcessingStatus } from 'shared/interfaces/Resource';
import { SmartAssistantVisualConfig } from 'shared/interfaces/SmartAssistant';

interface ChatHeaderProps extends FlexProps {
  smartAssistantId?: string;
  smartAssistantVisualConfig?: SmartAssistantVisualConfig;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  showRessourceProcessing?: boolean;
}

export function ChatHeader({
  smartAssistantId,
  smartAssistantVisualConfig,
  leftSection,
  rightSection,
  showRessourceProcessing = false,
  ...flexProps
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [resourcesProcessed, setResourcesProcessed] = useState(true);

  useQuery({
    queryKey: [SMART_ASSISTANT_RESOURCES, { smartAssistantId }],
    queryFn: async () =>
      showRessourceProcessing && smartAssistantId
        ? await SmartAssistantService.getSmartAssistantResources(smartAssistantId)
        : [],
    onSuccess: (resources: Resource[]) => {
      for (const resource of resources) {
        if (
          resource.aiProcessingStatus === ResourceAiProcessingStatus.IN_PROGRESS ||
          resource.aiProcessingStatus === ResourceAiProcessingStatus.FAILED
        ) {
          setResourcesProcessed(false);
          return;
        }
      }
      setResourcesProcessed(true);
    }
  });

  const textColor = theme.other.getReadableColor(smartAssistantVisualConfig?.secondaryBackgroundColor);

  return (
    <Flex
      direction="row"
      align="center"
      justify={{ base: !leftSection && !rightSection ? 'flex-start' : 'space-between', md: 'space-between' }}
      w="100%"
      pb={12}
      pt={16}
      px="0.75rem"
      bg={smartAssistantVisualConfig?.secondaryBackgroundColor}
      {...flexProps}>
      {leftSection ?? <Box />}

      <Flex direction="row" align="center" justify="center">
        {!!smartAssistantVisualConfig?.assistantAvatar &&
          cloneElement(smartAssistantVisualConfig.assistantAvatar, {
            size: 30,
            radius: 15
          })}
        <Space w={10} />
        <Title order={3} color={textColor} truncate maw="50vw">
          {smartAssistantVisualConfig?.assistantName}
        </Title>
        {!resourcesProcessed && (
          <>
            <Space w={10} />
            <Tooltip
              position="bottom"
              multiline
              p={0}
              keepMounted
              width={180}
              label={
                <Paper
                  h="100%"
                  bg="white"
                  withBorder
                  p="sm"
                  style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                  <Text>{t('SMART_ASSISTANT.RESOURCES_SECTION.WARNING_RESOURCE_AI_PROCESSING')}</Text>
                </Paper>
              }
              withinPortal>
              <IconAlertTriangle color="orange" style={{ width: 20, height: 20 }} />
            </Tooltip>
          </>
        )}
      </Flex>

      {rightSection ?? <Box />}
    </Flex>
  );
}
